import { useEffect, useState } from "react";
import { RefreshCw, Zap, Webhook, RotateCcw, Copy, CheckCircle2, XCircle } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

export default function ShiprocketPanel() {
  const [ov, setOv] = useState(null);
  const [logs, setLogs] = useState(null);
  const [busy, setBusy] = useState("");
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/courier-status`;

  const load = () => {
    api.get("/admin/shiprocket/overview").then(({ data }) => setOv(data)).catch((e) => toast.error(e.friendlyMessage));
    api.get("/admin/shiprocket/logs").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  };
  useEffect(() => { load(); }, []);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      toast.success(data.message || okMsg);
      load();
      return data;
    } catch (e) { toast.error(e.friendlyMessage); } finally { setBusy(""); }
  };

  const copy = () => { navigator.clipboard.writeText(webhookUrl); toast.success("Webhook URL copied"); };

  return (
    <div className="bg-white border border-[#E5DEC9] p-6 mt-6" data-testid="shiprocket-panel">
      <p className="font-serif text-xl mb-1">Shiprocket Sync & Webhook</p>
      <p className="text-xs text-[#8C7A5E] mb-4">Real-time courier status sync. The webhook URL below intentionally avoids restricted keywords (shiprocket / kartrocket / sr / kr) so Shiprocket accepts it.</p>

      <div className="bg-[#F4EFE6] border border-[#D4AF37]/40 p-3 mb-5">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#8C7A5E] mb-1">Webhook URL — paste in Shiprocket → Settings → Webhooks</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-white px-3 py-2 truncate" data-testid="sr-webhook-url">{webhookUrl}</code>
          <button onClick={copy} data-testid="sr-webhook-copy" className="tq-btn-outline !py-2 !px-3" aria-label="Copy webhook URL"><Copy size={13} /></button>
        </div>
        <p className="text-[10px] text-[#9a938a] mt-1.5">Header to add in Shiprocket: <code className="bg-white px-1">x-api-key: &lt;your Webhook Secret above&gt;</code>. Verified on every event; duplicates and floods are auto-blocked.</p>
      </div>

      {!ov ? <div className="h-24 bg-[#F4EFE6] animate-pulse" /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              ["Connection", ov.connected ? "Connected" : ov.credentials_set ? "Configured" : "Not configured", ov.connected],
              ["Pending Tracking", ov.pending_tracking, null],
              ["Orders Not Pushed", ov.unsynced_orders, null],
              ["Sync Errors", ov.error_count, ov.error_count === 0],
            ].map(([l, v, ok]) => (
              <div key={l} className="border border-[#F4EFE6] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#9a938a]">{l}</p>
                <p className="text-sm font-semibold flex items-center gap-1.5 mt-1">
                  {ok === true && <CheckCircle2 size={13} className="text-[#1A3C34]" />}
                  {ok === false && <XCircle size={13} className="text-[#8B1E21]" />}
                  <span data-testid={`sr-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</span>
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6E6D6A] mb-4" data-testid="sr-last-sync">Last webhook event: {ov.last_webhook_at ? new Date(ov.last_webhook_at).toLocaleString("en-IN") : "none yet"}</p>
          <div className="flex flex-wrap gap-2 mb-5">
            <button onClick={() => act("test", () => api.post("/admin/shiprocket/test"), "Connection OK")} disabled={!!busy} data-testid="sr-test-connection" className="tq-btn-dark !py-2.5"><Zap size={13} /> {busy === "test" ? "Testing…" : "Test Connection"}</button>
            <button onClick={() => act("sync", () => api.post("/admin/shiprocket/sync-orders"), "Orders synced")} disabled={!!busy} data-testid="sr-sync-now" className="tq-btn-outline !py-2.5"><RefreshCw size={13} /> {busy === "sync" ? "Syncing…" : "Sync Orders Now"}</button>
            <button onClick={() => act("webhook", () => api.post("/admin/shiprocket/test-webhook"), "Webhook verified")} disabled={!!busy} data-testid="sr-test-webhook" className="tq-btn-outline !py-2.5"><Webhook size={13} /> {busy === "webhook" ? "Testing…" : "Test Webhook"}</button>
            <button onClick={() => act("retry", () => api.post("/admin/shiprocket/retry-failed"), "Retry complete")} disabled={!!busy} data-testid="sr-retry" className="tq-btn-outline !py-2.5"><RotateCcw size={13} /> Retry Failed Sync</button>
          </div>
        </>
      )}

      <p className="text-xs font-bold uppercase tracking-wider text-[#8C7A5E] mb-2">Sync & Webhook Logs</p>
      {!logs ? <div className="h-16 bg-[#F4EFE6] animate-pulse" /> : logs.length === 0 ? (
        <p className="text-xs text-[#9a938a] py-3" data-testid="sr-logs-empty">No log entries yet — webhook events and syncs appear here.</p>
      ) : (
        <div className="max-h-56 overflow-y-auto border border-[#F4EFE6] divide-y divide-[#F9F5EC]">
          {logs.map((l) => (
            <div key={l.id} className="px-3 py-2 text-xs flex items-start gap-2" data-testid={`sr-log-${l.id}`}>
              <span className={`uppercase font-bold tracking-wider w-10 shrink-0 ${l.level === "error" ? "text-[#8B1E21]" : l.level === "warn" ? "text-[#B8860B]" : "text-[#1A3C34]"}`}>{l.level}</span>
              <span className="flex-1">{l.message}{l.detail ? <span className="text-[#9a938a]"> — {l.detail}</span> : ""}</span>
              <span className="text-[#9a938a] shrink-0">{new Date(l.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
